import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import PageTitle from '../shared/PageTitle';
import { scrollToSectionTop } from '../../utils/scrollUtils';

const SupportTickets = () => {
  // State variables
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Helper function to get filtered tickets based on the current filter state
  const getFilteredTickets = () => {
    if (filter === 'all') return tickets;
    return tickets.filter(ticket => ticket.status.toLowerCase() === filter.toLowerCase());
  };

  // Derived state for pagination, based on filtered tickets
  const filteredTickets = getFilteredTickets();
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTickets.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);

  // Pagination actions (functions)
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    setTimeout(() => {
      scrollToSectionTop('support-tickets-section');
    }, 50);
  };
  const previousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setTimeout(() => {
        scrollToSectionTop('support-tickets-section');
      }, 50);
    }
  };
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setTimeout(() => {
        scrollToSectionTop('support-tickets-section');
      }, 50);
    }
  };

  // Effect to reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  // Fetch tickets on component mount
  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/support/tickets', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.data.success) {
        setTickets(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching support tickets:', error);
      // toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const updateTicketStatus = async (ticketId, status, notes = '') => {
    try {
      setUpdatingStatus(true);
      await axios.put(`/api/support/tickets/${ticketId}/status`, {
        status,
        notes
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success('Ticket status updated successfully');
      fetchTickets(); // Re-fetch to get latest status
      setShowModal(false);
      setSelectedTicket(null);
    } catch (error) {
      console.error('Error updating ticket status:', error);
      toast.error('Failed to update ticket status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Open': return 'bg-danger';
      case 'In Progress': return 'bg-warning text-dark';
      case 'Resolved': return 'bg-success';
      case 'Closed': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case 'High': return 'bg-danger';
      case 'Medium': return 'bg-warning text-dark';
      case 'Low': return 'bg-info';
      default: return 'bg-secondary';
    }
  };

  function formatDate(dateString) {
    if (!dateString || typeof dateString !== 'string') return 'N/A';

    try {
      // Parse "YYYY-MM-DD HH:mm:ss" as UTC
      const [datePart, timePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = timePart.split(':').map(Number);

      // Create a UTC Date object
      const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

      // Format to IST using timeZone
      return new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(utcDate);
    } catch (err) {
      console.error('Invalid date string:', dateString, err);
      return 'Invalid Date';
    }
  }

  const TicketModal = () => {
    const [newStatus, setNewStatus] = useState(selectedTicket?.status || 'Open');
    const [notes, setNotes] = useState('');

    if (!selectedTicket) return null;

    return (
      <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title text-dark">
                <i className="fas fa-ticket-alt me-2"></i>
                Support Ticket Details
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
            </div>
            <div className="modal-body">
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Ticket ID:</strong> TKT-{String(selectedTicket.id).padStart(6, '0')}
                </div>
                <div className="col-md-6">
                  <strong>Created:</strong> {formatDate(selectedTicket.created_at)}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Priority:</strong>
                  <span className={`badge ${getPriorityBadgeClass(selectedTicket.priority)} ms-2`}>
                    {selectedTicket.priority}
                  </span>
                </div>
                <div className="col-md-6">
                  <strong>Current Status:</strong>
                  <span className={`badge ${getStatusBadgeClass(selectedTicket.status)} ms-2`}>
                    {selectedTicket.status}
                  </span>
                </div>
              </div>

              <div className="mb-3">
                <strong>Subject:</strong>
                <p className="mt-1">{selectedTicket.subject}</p>
              </div>

              <div className="mb-3">
                <strong>Description:</strong>
                <p className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{selectedTicket.description}</p>
              </div>

              <div className="row mb-3">
                <div className="col-md-4">
                  <strong>User:</strong> {selectedTicket.user_name || 'Unknown'}
                </div>
                <div className="col-md-4">
                  <strong>Email:</strong> {selectedTicket.user_email || 'N/A'}
                </div>
                <div className="col-md-4">
                  <strong>Role:</strong> {selectedTicket.user_role || 'Unknown'}
                </div>
              </div>

              <div className="mb-3">
                <strong>Organization:</strong> {selectedTicket.organization_name || 'Unknown'}
              </div>

              {selectedTicket.notes && (
                <div className="mb-3">
                  <strong>Notes:</strong>
                  <p className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{selectedTicket.notes}</p>
                </div>
              )}

              <hr />

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Update Status</label>
                    <select
                      className="form-select"
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                    >
                      <option value="Open">Open</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Resolved">Resolved</option>
                      <option value="Closed">Closed</option>
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Notes (Optional)</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add notes about the status update..."
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => updateTicketStatus(selectedTicket.id, newStatus, notes)}
                disabled={updatingStatus}
              >
                {updatingStatus ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2"></i>
                    Update Status
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <PageTitle
        icon="fa-headset"
        title="Support Ticket Management"
        className="mb-4"
      />

      {/* Filter Tabs */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('all')}
                >
                  All Tickets ({tickets.length})
                </button>
                <button
                  type="button"
                  className={`btn ${filter === 'open' ? 'btn-danger' : 'btn-outline-danger'}`}
                  onClick={() => setFilter('open')}
                >
                  Open ({tickets.filter(t => t.status === 'Open').length})
                </button>
                <button
                  type="button"
                  className={`btn ${filter === 'in progress' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setFilter('in progress')}
                >
                  In Progress ({tickets.filter(t => t.status === 'In Progress').length})
                </button>
                <button
                  type="button"
                  className={`btn ${filter === 'resolved' ? 'btn-success' : 'btn-outline-success'}`}
                  onClick={() => setFilter('resolved')}
                >
                  Resolved ({tickets.filter(t => t.status === 'Resolved').length})
                </button>
                <button
                  type="button"
                  className={`btn ${filter === 'closed' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                  onClick={() => setFilter('closed')}
                >
                  Closed ({tickets.filter(t => t.status === 'Closed').length})
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="row" id="support-tickets-section">
        <div className="col-12">
          <div className="card shadow">
            <div className="py-3 superadmin-section">
              <h6 className="m-0 font-weight-bold text-primary">
                {filter === 'all' ? 'All Support Tickets' : `${filter.charAt(0).toUpperCase() + filter.slice(1)} Tickets`}
              </h6>
            </div>
            <div className="card-body">
              {filteredTickets.length === 0 ? (
                <div className="text-center py-4">
                  <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                  <p className="text-muted">No tickets found</p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Ticket ID</th>
                          <th>Subject</th>
                          <th>User</th>
                          <th>Organization</th>
                          <th>Priority</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.map(ticket => (
                          <tr key={ticket.id}>
                            <td className="font-monospace">
                              TKT-{String(ticket.id).padStart(6, '0')}
                            </td>
                            <td>
                              <div className="text-truncate" style={{ maxWidth: '200px' }}>
                                {ticket.subject}
                              </div>
                            </td>
                            <td>
                              <div>
                                <div className="fw-bold">{ticket.user_name || 'Unknown'}</div>
                                <small className="text-muted">{ticket.user_email || 'No email'}</small>
                              </div>
                            </td>
                            <td>{ticket.organization_name || 'Unknown'}</td>
                            <td>
                              <span className={`badge ${getPriorityBadgeClass(ticket.priority)}`}>
                                {ticket.priority}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${getStatusBadgeClass(ticket.status)}`}>
                                {ticket.status}
                              </span>
                            </td>
                            <td>
                              <small>{formatDate(ticket.created_at)}</small>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                  setSelectedTicket(ticket);
                                  setShowModal(true);
                                }}
                              >
                                <i className="fas fa-eye me-1"></i>
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {tickets.length > itemsPerPage && (
                    <div className="card-footer d-flex justify-content-between align-items-center">
                      <div>
                        Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredTickets.length)} of {filteredTickets.length} tickets
                      </div>

                      <nav aria-label="Visitor pagination">
                        <ul className="pagination mb-0">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={previousPage} disabled={currentPage === 1}>
                              <i className="fas fa-chevron-left"></i>
                            </button>
                          </li>

                          {/* Simplified pagination - show only certain page numbers */}
                          {Array.from({ length: totalPages }).map((_, index) => {
                            const pageNumber = index + 1;

                            // Show first page, last page, current page, and pages around current page
                            if (
                              pageNumber === 1 ||
                              pageNumber === totalPages ||
                              (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                            ) {
                              return (
                                <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                                  <button className="page-link" onClick={() => paginate(pageNumber)}>
                                    {pageNumber}
                                  </button>
                                </li>
                              );
                            }

                            // Show ellipsis
                            if (
                              pageNumber === 2 && currentPage > 3 ||
                              pageNumber === totalPages - 1 && currentPage < totalPages - 2
                            ) {
                              return (
                                <li key={pageNumber} className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              );
                            }

                            return null;
                          })}

                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={nextPage} disabled={currentPage === totalPages}>
                              <i className="fas fa-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && <TicketModal />}
    </div>
  );
};

export default SupportTickets; 